import { createElement, type MouseEvent, type ReactNode, useMemo } from 'react';
import { useRouter } from 'expo-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  isInternalBlockHref,
  prepareChatMarkdown,
  type ContextLinkTarget,
} from '@/lib/chat/context-links';

type ChatMarkdownProps = {
  content: string;
  linkTargets?: ContextLinkTarget[];
};

function MarkdownLink({
  href,
  children,
  onInternalNavigate,
}: {
  href?: string;
  children?: ReactNode;
  onInternalNavigate: (href: string, event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  if (href && isInternalBlockHref(href)) {
    return createElement(
      'a',
      {
        href,
        className: 'cs-context-link',
        onClick: (event: MouseEvent<HTMLAnchorElement>) => onInternalNavigate(href, event),
      },
      children,
    );
  }

  return createElement(
    'a',
    {
      href,
      className: 'chat-markdown-external-link',
      target: '_blank',
      rel: 'noopener noreferrer',
    },
    children,
  );
}

export function ChatMarkdown({ content, linkTargets = [] }: ChatMarkdownProps) {
  const router = useRouter();
  const trimmed = content.trim();
  const prepared = useMemo(
    () => (trimmed ? prepareChatMarkdown(trimmed, linkTargets) : ''),
    [trimmed, linkTargets],
  );

  if (!prepared) {
    return null;
  }

  function onInternalNavigate(href: string, event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    router.push(href);
  }

  return createElement(
    'div',
    { className: 'chat-markdown' },
    createElement(ReactMarkdown, {
      remarkPlugins: [remarkGfm],
      components: {
        a: ({ href, children }) =>
          createElement(MarkdownLink, { href, children, onInternalNavigate }),
      },
      children: prepared,
    }),
  );
}
